import { useCallback, useMemo } from "react";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import {
  bulletsToActionQueue,
  bulletsToExecutiveInsightCards,
} from "@/utils/mistralInsightMappers";
import {
  Users,
  GraduationCap,
  Clock,
  ClipboardCheck,
  BarChart3,
} from "lucide-react";
import {
  buildProfileRingStyle,
  buildProfileHeroCopy,
  buildProfileHeroKpis,
  buildProfileGenderDonut,
  buildProfileAgeBars,
  buildProfileInsightCards,
  buildQualificationBars,
  buildSocialCategoryView,
  buildProfileActions,
  buildBlockProfileWatchlist,
  buildCTTeacherProfileMarkdown,
} from "@/utils/ctteacherProfileDerived";
import { toTitleCase } from "../utils/toTitleCase";

const KPI_ICONS = {
  "": Users,
  pink: Users,
  green: BarChart3,
  purple: Clock,
};

const INSIGHT_ICONS = {
  green: GraduationCap,
  purple: Clock,
  orange: ClipboardCheck,
};

const ACTION_ICONS = {
  purple: Clock,
  orange: ClipboardCheck,
  green: BarChart3,
};

const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const CTTeacherProfileTab = ({
  overview = {},
  genderData = [],
  ageData = null,
  socialCategory = [],
  qualification = null,
  blockData = [],
}) => {
  const hasData = Boolean(overview?.total_teachers);

  const buildLocalMarkdown = useCallback(
    () => buildCTTeacherProfileMarkdown(overview, qualification, ageData, blockData),
    [overview, qualification, ageData, blockData]
  );
  const scopeKey = useMemo(
    () => JSON.stringify({ t: overview?.total_teachers, f: overview?.female_pct }),
    [overview?.total_teachers, overview?.female_pct]
  );
  const {
    loading: aiLoading,
    pickFromBucket,
    pickPrimaryAction,
    pickMapped,
    isMistral,
  } = useMistralNarrativeOverlay({
    kind: "ctteacher-profile",
    title: "CT Teacher Workforce Profile",
    buildLocalMarkdown,
    context: { overview, qualification, ageData, blockData },
    enabled: hasData,
    scopeKey,
  });

  const profileRing = useMemo(() => buildProfileRingStyle(overview), [overview]);
  const localHeroCopy = useMemo(
    () => buildProfileHeroCopy(overview, ageData),
    [overview, ageData]
  );
  const heroCopy = useMemo(() => {
    if (aiLoading) return localHeroCopy;
    const narrative = pickFromBucket(localHeroCopy.narrative, "insights", 0);
    const primaryAction = pickPrimaryAction(localHeroCopy.primaryAction);
    const chips = isMistral
      ? [{ label: "Mistral AI Assisted", className: "purple" }, ...localHeroCopy.chips]
      : localHeroCopy.chips;
    return {
      ...localHeroCopy,
      narrative: narrative || localHeroCopy.narrative,
      primaryAction: primaryAction || localHeroCopy.primaryAction,
      chips,
    };
  }, [aiLoading, localHeroCopy, pickFromBucket, pickPrimaryAction, isMistral]);
  const heroKpis = useMemo(
    () => buildProfileHeroKpis(overview, ageData),
    [overview, ageData]
  );
  const genderDonut = useMemo(
    () => buildProfileGenderDonut(overview, genderData),
    [overview, genderData]
  );
  const ageBars = useMemo(() => buildProfileAgeBars(ageData), [ageData]);
  const localInsightCards = useMemo(
    () => buildProfileInsightCards(overview, qualification, ageData),
    [overview, qualification, ageData]
  );
  const insightCards = useMemo(
    () => pickMapped(localInsightCards, "insights", bulletsToExecutiveInsightCards),
    [pickMapped, localInsightCards]
  );
  const academicBars = useMemo(
    () => buildQualificationBars(qualification?.academic),
    [qualification]
  );
  const professionalBars = useMemo(
    () => buildQualificationBars(qualification?.professional),
    [qualification]
  );
  const socialView = useMemo(
    () => buildSocialCategoryView(socialCategory),
    [socialCategory]
  );
  const localActions = useMemo(
    () => buildProfileActions(overview, qualification, ageData, blockData),
    [overview, qualification, ageData, blockData]
  );
  const actions = useMemo(() => {
    const mapped = pickMapped(localActions, "actions", bulletsToActionQueue);
    return mapped.map((item, index) => ({
      ...localActions[index],
      title: item.title,
      body: item.body,
      iconClass: localActions[index]?.iconClass || item.iconClass,
    }));
  }, [pickMapped, localActions]);
  const watchlist = useMemo(
    () => buildBlockProfileWatchlist(blockData, overview, ageData, qualification),
    [blockData, overview, ageData, qualification]
  );

  return (
    <div className="ctpf-profile-tab" data-testid="ctteacher-profile">
      <section className="ctpf-hero-card" id="ctpf-hero">
        <div className="ctpf-profile-hero-layout">
          <div>
            <div className="ctpf-profile-score">
              <div
                className="ctpf-profile-ring"
                style={profileRing.style}
                aria-label={`Female teachers ${profileRing.femalePct} percent`}
              >
                <div>
                  <strong>{profileRing.femalePct}%</strong>
                  <span>Female Share</span>
                </div>
              </div>
              <div className="ctpf-hero-copy">
                <h2>{toTitleCase(heroCopy.headline)}</h2>
                <p>{heroCopy.narrative}</p>
                <div className="ctpf-status-row">
                  {heroCopy.chips.map((chip) => (
                    <span
                      key={chip.label}
                      className={`ctpf-status-chip ${chip.className}`.trim()}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="ctpf-summary-note">
              <div className="ctpf-icon-box purple">
                <Users size={22} />
              </div>
              <div>
                <b>Primary executive action</b>
                <span>{heroCopy.primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="ctpf-hero-kpis" aria-label="Teacher profile KPIs">
            {heroKpis.map((card) => {
              const Icon = KPI_ICONS[card.iconClass] ?? Users;
              return (
                <article key={card.label} className="ctpf-metric-card">
                  <div className={`ctpf-metric-icon ${card.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <small>{card.label}</small>
                  <strong>{card.value}</strong>
                  <span>{card.sub}</span>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <div className="ctpf-section-title">
        <div>
          <h2>Demographic Profile</h2>
          <p>
            Gender and age are separated into large, readable panels so users can immediately
            understand workforce composition and succession risk.
          </p>
        </div>
        <button
          type="button"
          className="ctpf-section-action"
          onClick={() => scrollToId("ctpf-qualification")}
        >
          View qualifications →
        </button>
      </div>

      <section className="ctpf-profile-lens">
        <article className="ctpf-panel">
          <div className="ctpf-panel-head">
            <div>
              <h3>Gender Distribution</h3>
              <p>Teacher records by gender with parity context.</p>
            </div>
            <span className="ctpf-pill">GPI {genderDonut.gpi}</span>
          </div>
          <div className="ctpf-gender-panel-layout">
            <div className="ctpf-donut-large" style={genderDonut.style} aria-hidden="true">
              <div className="ctpf-donut-center">
                <div>
                  <strong>{genderDonut.total.toLocaleString("en-IN")}</strong>
                  <span>Total records</span>
                </div>
              </div>
            </div>
            <div className="ctpf-legend-grid">
              {genderDonut.legend.map((row) => (
                <div key={row.label} className="ctpf-legend-card">
                  <i className={`ctpf-dot ${row.dotClass}`.trim()} />
                  <div>
                    <b>{row.label}</b>
                    <span>{row.hint}</span>
                  </div>
                  <strong>{row.value.toLocaleString("en-IN")}</strong>
                </div>
              ))}
            </div>
          </div>
        </article>

        <article className="ctpf-panel">
          <div className="ctpf-panel-head">
            <div>
              <h3>Age Distribution</h3>
              <p>
                Median age {ageBars.medianAge} years · ageing risk {ageBars.ageingRisk}%.
              </p>
            </div>
            <span className="ctpf-pill amber">Succession lens</span>
          </div>
          <div className="ctpf-age-bars" aria-label="Age distribution bar chart">
            {ageBars.bars.map((bar) => (
              <div key={bar.band} className="ctpf-age-bar-item">
                <div className="ctpf-age-bar" style={{ height: `${bar.height}px` }}>
                  <span>{bar.display}</span>
                </div>
                <div className="ctpf-age-label">{bar.band}</div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="ctpf-insight-grid">
        {insightCards.map((card) => {
          const Icon = INSIGHT_ICONS[card.iconClass] ?? GraduationCap;
          return (
            <article key={card.id} className="ctpf-insight-card">
              <div className={`ctpf-icon-box ${card.iconClass}`.trim()}>
                <Icon size={22} />
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <p>{card.body}</p>
            </article>
          );
        })}
      </section>

      <div className="ctpf-section-title" id="ctpf-qualification">
        <div>
          <h2>Qualifications and Category Mix</h2>
          <p>
            The qualification charts use common bar styling and remove visual clutter from the
            original tab while preserving all major categories.
          </p>
        </div>
        <button
          type="button"
          className="ctpf-section-action"
          onClick={() => scrollToId("ctpf-actions")}
        >
          View action queue →
        </button>
      </div>

      <section className="ctpf-qualification-grid">
        <article className="ctpf-panel">
          <div className="ctpf-panel-head">
            <div>
              <h3>Academic Qualification</h3>
              <p>Highest academic qualification distribution.</p>
            </div>
            <span className="ctpf-pill">Top categories</span>
          </div>
          <div className="ctpf-hbar-list">
            {academicBars.map((row) => (
              <div key={row.label} className="ctpf-hbar-row">
                <div className="ctpf-hbar-label">{row.label}</div>
                <div className="ctpf-hbar-track">
                  <div className="ctpf-hbar" style={{ width: `${row.width}%` }} />
                </div>
                <div className="ctpf-hbar-value">{row.display}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="ctpf-panel">
          <div className="ctpf-panel-head">
            <div>
              <h3>Professional Qualification</h3>
              <p>Teacher education and certification status.</p>
            </div>
            <span className="ctpf-pill green">Certification lens</span>
          </div>
          <div className="ctpf-hbar-list">
            {professionalBars.map((row) => (
              <div key={row.label} className="ctpf-hbar-row">
                <div className="ctpf-hbar-label">{row.label}</div>
                <div className="ctpf-hbar-track">
                  <div
                    className={`ctpf-hbar ${row.barClass}`.trim()}
                    style={{ width: `${row.width}%` }}
                  />
                </div>
                <div className="ctpf-hbar-value">{row.display}</div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="ctpf-panel">
        <div className="ctpf-panel-head">
          <div>
            <h3>Social Category Distribution</h3>
            <p>
              Compact composition view with readable legend and executive-level proportion
              estimates.
            </p>
          </div>
          <span className="ctpf-pill">Representation mix</span>
        </div>
        <div className="ctpf-social-layout">
          <div className="ctpf-social-donut" style={socialView.style} aria-hidden="true" />
          <div className="ctpf-category-list">
            {socialView.segments.map((seg) => (
              <div key={seg.category} className="ctpf-category-item">
                <span>
                  <i className="ctpf-dot" style={{ background: seg.color }} />
                  {seg.category}
                </span>
                <b>{seg.pct}%</b>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="ctpf-section-title" id="ctpf-actions">
        <div>
          <h2>Profile Action Queue</h2>
          <p>
            Recommended next actions convert the profile analysis into operational follow-up for
            district and block officers.
          </p>
        </div>
        <button
          type="button"
          className="ctpf-section-action"
          onClick={() => scrollToId("ctpf-watchlist")}
        >
          Go to block watchlist →
        </button>
      </div>

      <section className="ctpf-two-column">
        <article className="ctpf-panel">
          <div className="ctpf-panel-head">
            <div>
              <h3>Recommended Actions</h3>
              <p>Focus on measurable workforce profile improvements.</p>
            </div>
            <span className="ctpf-pill amber">Priority</span>
          </div>
          <div className="ctpf-risk-list">
            {actions.map((item) => {
              const Icon = ACTION_ICONS[item.iconClass] ?? Clock;
              return (
                <div key={item.title} className="ctpf-risk-item">
                  <div className={`ctpf-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                  <span className={`ctpf-priority ${item.priorityClass}`.trim()}>
                    {item.priority}
                  </span>
                </div>
              );
            })}
          </div>
        </article>

        <article className="ctpf-panel ctpf-table-card" id="ctpf-watchlist">
          <div className="ctpf-table-head">
            <div>
              <h3>Block Profile Watchlist</h3>
              <p>Sample monitoring layout for district review.</p>
            </div>
            <span className="ctpf-pill">Top risk view</span>
          </div>
          <table aria-label="Block profile watchlist">
            <thead>
              <tr>
                <th>Block</th>
                <th>Teachers</th>
                <th>GPI</th>
                <th>Avg Age</th>
                <th>55+ Risk</th>
                <th>Untrained</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {watchlist.map((row) => (
                <tr key={row.block}>
                  <td>{row.block}</td>
                  <td>{row.teachers?.toLocaleString("en-IN")}</td>
                  <td>{row.gpi}</td>
                  <td>{row.avgAge}</td>
                  <td>{row.ageingRisk}</td>
                  <td>{row.untrained}</td>
                  <td>
                    <span className={`ctpf-tag ${row.statusClass}`.trim()}>{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      </section>
    </div>
  );
};

export default CTTeacherProfileTab;
